import { Button, Stack } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";

import { Typography, useSnackbar } from "@/components/ui-v2";

const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

const NewVersionMessage = () => {
  return (
    <Stack
      spacing={3}
      direction="row"
      sx={{
        alignItems: "center",
      }}
    >
      <Typography>New version of Layer is available!</Typography>
      <Button
        variant="outlined"
        color="inherit"
        onClick={() => window.location.assign(window.location.href)}
      >
        Reload
      </Button>
    </Stack>
  );
};

const getVersion = async () => {
  const response = await fetch("/version");
  const newVersionHash = await response.text();
  const isValidVersion = /^\d+$/.test(newVersionHash);
  return isValidVersion ? newVersionHash : null;
};

export const VersionChecker = () => {
  const { showInfo } = useSnackbar();
  const versionHash = useRef<null | string>(null);
  const lastCheckTime = useRef<number>(0);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const checkVersion = useCallback(async () => {
    const currentTime = Date.now();

    // Check if enough time has passed since the last check
    if (currentTime - lastCheckTime.current < CHECK_INTERVAL) {
      return;
    }

    try {
      const newVersionHash = await getVersion();
      lastCheckTime.current = Date.now();

      if (!newVersionHash) {
        return;
      }

      const previousVersionSet = !!versionHash.current;
      const versionHashChanged = versionHash.current !== newVersionHash;

      if (previousVersionSet && versionHashChanged) {
        showInfo(<NewVersionMessage />, {
          autoHideDuration: 0,
        });

        // Clean up: remove focus listener and clear interval
        const intervalId = intervalIdRef.current;
        // Set to null first to prevent race conditions
        intervalIdRef.current = null;

        if (intervalId) {
          clearInterval(intervalId);
        }
        window.removeEventListener("focus", checkVersion);
        return;
      }

      versionHash.current = newVersionHash;
    } catch (error) {
      console.error("Failed to fetch version:", error);
    }
  }, [showInfo]);

  const pollGetVersion = useCallback(() => {
    // Document does not have focus
    if (!document.hasFocus()) {
      return;
    }

    checkVersion();
  }, [checkVersion]);

  useEffect(() => {
    // Set an interval to check the version every check interval
    intervalIdRef.current = setInterval(pollGetVersion, CHECK_INTERVAL);

    // Add event listener for window focus
    window.addEventListener("focus", checkVersion);

    // Run the version check when the component mounts
    checkVersion();

    return () => {
      // Cleanup on unmount: remove event listener and clear interval
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      window.removeEventListener("focus", checkVersion);
    };
  }, [checkVersion, pollGetVersion]);

  return null;
};
